import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import Header from '../../components/ui/Header';
import productService from '../../services/productService';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';

const ProductReviews = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchQuery, categoryFilter, brandFilter, sortBy]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService?.getAllProducts();
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(
        (p) =>
          p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          p?.brand?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          p?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered?.filter((p) => p?.category === categoryFilter);
    }

    // Brand filter
    if (brandFilter !== 'all') {
      filtered = filtered?.filter((p) => p?.brand === brandFilter);
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
        break;
      case 'price-low':
        filtered?.sort((a, b) => a?.currentPrice - b?.currentPrice);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.currentPrice - a?.currentPrice);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'name':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setBrandFilter('all');
    setSortBy('newest');
  };

  const uniqueBrands = [...new Set(products?.map((p) => p?.brand))]?.sort();
  const uniqueCategories = [...new Set(products?.map((p) => p?.category))]?.sort();

  const hasActiveFilters = searchQuery || categoryFilter !== 'all' || brandFilter !== 'all' || sortBy !== 'newest';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-text-primary mb-4">Product Reviews</h1>
            <p className="text-lg text-muted-foreground">
              Browse our comprehensive collection of expert-tested vacuum cleaner reviews
            </p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl shadow-subtle border border-border p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <Select
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value)}
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...uniqueCategories?.map((cat) => ({
                    value: cat,
                    label: cat?.replace(/_/g, ' ')?.replace(/\b\w/g, (l) => l?.toUpperCase()),
                  })),
                ]}
              />

              {/* Brand Filter */}
              <Select
                value={brandFilter}
                onChange={(value) => setBrandFilter(value)}
                options={[
                  { value: 'all', label: 'All Brands' },
                  ...uniqueBrands?.map((brand) => ({ value: brand, label: brand })),
                ]}
              />
            </div>

            <div className="flex items-center justify-between">
              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <Select
                  value={sortBy}
                  onChange={(value) => setSortBy(value)}
                  options={[
                    { value: 'newest', label: 'Newest First' },
                    { value: 'name', label: 'Name (A-Z)' },
                    { value: 'price-low', label: 'Price: Low to High' },
                    { value: 'price-high', label: 'Price: High to Low' },
                    { value: 'rating', label: 'Highest Rated' },
                  ]}
                  className="w-48"
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters} iconName="X" iconPosition="left">
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-sm text-muted-foreground">
              Showing {filteredProducts?.length} of {products?.length} products
            </p>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)]?.map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-xl p-6 border border-border">
                  <div className="aspect-square bg-muted rounded-lg mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-4 bg-muted rounded w-full" />
                </div>
              ))}
            </div>
          ) : filteredProducts?.length === 0 ? (
            <div className="bg-white rounded-xl shadow-subtle border border-border p-12 text-center">
              <Filter className="mx-auto mb-4 text-muted-foreground" size={48} />
              <h3 className="text-xl font-semibold text-text-primary mb-2">No products found</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your filters or search query</p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear All Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts?.map((product) => (
                <Link
                  key={product?.id}
                  to={`/product-review/${product?.slug}`}
                  className="group bg-white rounded-xl shadow-subtle border border-border hover:shadow-medium transition-all duration-300 overflow-hidden"
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={product?.imageUrl}
                      alt={product?.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product?.badge && (
                      <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium">
                        {product?.badge?.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-primary">{product?.brand}</span>
                      <span className="text-xs text-muted-foreground">
                        {product?.category?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-text-primary mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {product?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product?.description}</p>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        {[...Array(5)]?.map((_, i) => (
                          <span
                            key={i}
                            className={`text-sm ${
                              i < Math.floor(product?.rating) ? 'text-accent' : 'text-muted-foreground'
                            }`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {product?.rating} ({product?.reviewCount} reviews)
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-text-primary">${product?.currentPrice}</span>
                        {product?.originalPrice && (
                          <span className="text-sm text-muted-foreground line-through ml-2">
                            ${product?.originalPrice}
                          </span>
                        )}
                      </div>
                      {product?.discount > 0 && (
                        <span className="text-sm font-medium text-success">Save {product?.discount}%</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductReviews;
